import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
const SCAN_COUNT_KEY = 'milestone_review_scan_count';
const MILESTONE_THRESHOLDS = [20, 50, 100];
interface MilestoneReviewContextType {
  scanCount: number;
  isModalOpen: boolean;
  closeModal: () => void;
  currentMilestone: 20 | 50 | 100 | null;
}
const MilestoneReviewContext = createContext<MilestoneReviewContextType>({
  scanCount: 0,
  isModalOpen: false,
  closeModal: () => {},
  currentMilestone: null
});
export function useMilestoneReview() {
  return useContext(MilestoneReviewContext);
}
interface MilestoneReviewProviderProps {
  children: ReactNode;
}
export function MilestoneReviewProvider({
  children
}: MilestoneReviewProviderProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState<20 | 50 | 100 | null>(null);
  const [scanCount, setScanCount] = useState(() => {
    return parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
  });
  const checkMilestones = useCallback((count: number) => {
    // Find the highest milestone reached that hasn't been shown yet
    // Sort descending to show the highest applicable milestone first if multiple were skipped
    const reachedMilestones = [...MILESTONE_THRESHOLDS].sort((a, b) => b - a).filter(m => count >= m);
    for (const milestone of reachedMilestones) {
      const shownKey = `milestone_review_shown_${milestone}`;
      const alreadyShown = localStorage.getItem(shownKey) === 'true';
      if (!alreadyShown) {
        setCurrentMilestone(milestone as 20 | 50 | 100);
        setTimeout(() => {
          setIsModalOpen(true);
        }, 1500);
        break; // Only show one at a time
      }
    }
  }, []);
  // Check on mount if we should show the modal
  useEffect(() => {
    const count = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
    checkMilestones(count);
  }, [checkMilestones]);
  // Listen for custom 'scanCompleted' events dispatched by GoogleReviewTrigger
  useEffect(() => {
    const handleScanCompleted = () => {
      const currentCount = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
      const newCount = currentCount + 1;
      localStorage.setItem(SCAN_COUNT_KEY, String(newCount));
      setScanCount(newCount);
      checkMilestones(newCount);
    };
    window.addEventListener('scanCompleted', handleScanCompleted);
    return () => window.removeEventListener('scanCompleted', handleScanCompleted);
  }, [checkMilestones]);
  const handleClose = useCallback(() => {
    if (currentMilestone) {
      localStorage.setItem(`milestone_review_shown_${currentMilestone}`, 'true');
    }
    setIsModalOpen(false);
    // Check if there are other milestones to show after a delay
    setTimeout(() => {
      const count = parseInt(localStorage.getItem(SCAN_COUNT_KEY) || '0', 10);
      checkMilestones(count);
    }, 1000);
  }, [currentMilestone, checkMilestones]);
  return <MilestoneReviewContext.Provider value={{
    scanCount,
    isModalOpen,
    closeModal: handleClose,
    currentMilestone
  }} data-id="element-3341">
      {children}
    </MilestoneReviewContext.Provider>;
}